/**
 * Drag state for the sectioned rail (spec §2.4): while a drag is live the rail
 * renders a draft layout; a drop commits the draft through one write, a cancel
 * (Escape, or a drop on nothing) discards it.
 *
 * Node kinds:
 * - `row` — a starred row, registered with the container it sits in;
 * - `section`, `space` — top-level-only items, sortable among the top level;
 * - `rows` — a container's rows area: a section's rows, or a parent's sub-items;
 * - `slot` — a section's header, standing for "the top level, just above this section".
 */
import { useState } from 'react';

import { closestCenter, PointerSensor, pointerWithin, useSensor, useSensors } from '@dnd-kit/core';

import { containerOf, indexOfItem, ROOT, type RailLayout } from '@shared/navigation/railLayout';

import { moveRowToContainer, reorderWithin } from './railDrag';

import type {
  Collision,
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DroppableContainer,
  Over,
} from '@dnd-kit/core';

/** What a sortable or droppable node is. */
export type RailNodeKind = 'row' | 'section' | 'space' | 'rows' | 'slot';

/** The `data` every rail node registers. */
export interface RailNodeData {
  kind: RailNodeKind;
  /** `row`: the container it sits in. `rows`: the container it receives into. */
  container?: string;
  /** `slot`: the section it stands above. */
  before?: string;
}

/** The data a dnd-kit node registered, if any. */
function dataOf(
  node: { data: { current?: unknown } } | null | undefined
): RailNodeData | undefined {
  return node?.data.current as RailNodeData | undefined;
}

/** Whether a dragged node can only live at the top level. */
function isTopLevelOnly(kind: RailNodeKind | undefined): boolean {
  return kind === 'section' || kind === 'space';
}

/** Whether a node is a top-level item: a section, a space, or a row outside every section. */
function isTopLevel(node: DroppableContainer): boolean {
  const data = dataOf(node);
  return isTopLevelOnly(data?.kind) || (data?.kind === 'row' && data.container === ROOT);
}

/** Hits of the most specific kind: rows and spaces, then header slots, then rows areas. */
function mostSpecific(hits: Collision[], targets: DroppableContainer[]): Collision[] {
  const kindOfHit = (hit: Collision) => dataOf(targets.find(c => c.id === hit.id))?.kind;
  for (const kinds of [['row', 'space'], ['slot'], ['rows']] as const) {
    const matching = hits.filter(hit =>
      (kinds as readonly string[]).includes(kindOfHit(hit) ?? '')
    );
    if (matching.length > 0) return matching;
  }
  return [];
}

/**
 * A dragged section or space collides only with top-level items. A dragged row
 * prefers the row or space under the pointer, then a header (the slot above that
 * section), then a rows area; with nothing under the pointer, the nearest row.
 */
export const railCollision: CollisionDetection = args => {
  const containers = args.droppableContainers;
  if (isTopLevelOnly(dataOf(args.active)?.kind)) {
    return closestCenter({ ...args, droppableContainers: containers.filter(isTopLevel) });
  }
  const targets = containers.filter(c => dataOf(c)?.kind !== 'section');
  const hits = mostSpecific(pointerWithin({ ...args, droppableContainers: targets }), targets);
  if (hits.length > 0) return hits;
  return closestCenter({
    ...args,
    droppableContainers: targets.filter(c => dataOf(c)?.kind === 'row'),
  });
};

/** Where a dragged row would land: a container, and the item to land before. */
interface Placement {
  container: string;
  beforeId?: string;
}

/** Over a row or a space: its own container, just before it. */
function placeBeforeItem(draft: RailLayout, overId: string): Placement | undefined {
  const container = containerOf(draft, overId);
  return container === undefined ? undefined : { container, beforeId: overId };
}

/** Where a row dragged over `over` would go. */
function placementFor(draft: RailLayout, over: Over): Placement | undefined {
  const data = dataOf(over);
  switch (data?.kind) {
    case 'row':
    case 'space':
      return placeBeforeItem(draft, String(over.id));
    case 'slot':
      return data.before ? { container: ROOT, beforeId: data.before } : undefined;
    case 'rows':
      return data.container ? { container: data.container } : undefined;
    default:
      return undefined;
  }
}

/** Whether row `rowId` already sits at the top level directly above item `beforeId`. */
function isJustAbove(draft: RailLayout, rowId: string, beforeId: string): boolean {
  return (
    containerOf(draft, rowId) === ROOT &&
    indexOfItem(draft, rowId) === indexOfItem(draft, beforeId) - 1
  );
}

/** The draft after a row passes over `over`, or `draft` itself when nothing should move yet. */
function draftAfterRowOver(draft: RailLayout, rowId: string, over: Over): RailLayout {
  const placement = placementFor(draft, over);
  if (placement === undefined) return draft;
  if (dataOf(over)?.kind === 'slot') {
    const beforeId = placement.beforeId ?? '';
    return isJustAbove(draft, rowId, beforeId)
      ? draft
      : moveRowToContainer(draft, rowId, ROOT, beforeId);
  }
  if (placement.container === containerOf(draft, rowId)) return draft; // same container: sorted on drop
  // moveRowToContainer refuses a parent into sub-items (one level deep), returning `draft` itself.
  return moveRowToContainer(draft, rowId, placement.container, placement.beforeId);
}

/** The top-level item a dragged section or space was dropped on. */
function topLevelTarget(over: Over): string {
  const data = dataOf(over);
  return data?.kind === 'slot' && data.before ? data.before : String(over.id);
}

/** Sensors, the layout to render, what is being dragged, and the DndContext handlers. */
export function useRailDrag(layout: RailLayout, commit: (next: RailLayout) => void) {
  // 5px before a drag starts, so a plain click still opens the row.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [draft, setDraft] = useState<RailLayout | null>(null);
  const [dragKind, setDragKind] = useState<RailNodeKind | null>(null);

  const end = () => {
    setDraft(null);
    setDragKind(null);
  };

  const onDragStart = ({ active }: DragStartEvent) => {
    setDraft(layout);
    setDragKind(dataOf(active)?.kind ?? null);
  };

  const onDragOver = ({ active, over }: DragOverEvent) => {
    if (draft === null || over === null || dataOf(active)?.kind !== 'row') return;
    const next = draftAfterRowOver(draft, String(active.id), over);
    if (next !== draft) setDraft(next);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (draft !== null && over !== null) {
      const topLevelOnly = isTopLevelOnly(dataOf(active)?.kind);
      const target = topLevelOnly ? topLevelTarget(over) : String(over.id);
      const next = reorderWithin(draft, String(active.id), target);
      if (JSON.stringify(next) !== JSON.stringify(layout)) commit(next);
    }
    end();
  };

  return {
    sensors,
    items: draft ?? layout,
    dragKind,
    handlers: { onDragStart, onDragOver, onDragEnd, onDragCancel: end },
  };
}
